import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LandingHeader from '../00-shared/LandingHeader';
import LandingFooter from '../00-shared/LandingFooter';
import MockInjuryRiskPreview from '../00-shared/MockInjuryRiskPreview';
import MockSessionCheckIn from '../00-shared/MockSessionCheckIn';
import { GlobalStyles } from '../00-shared/styles';
import { highlightColor } from '../00-shared/constants';
import { getBlogPostBySlug, blogPosts } from './blogData';

const VISUAL_COMPONENTS: Record<string, React.FC> = {
    'injury-risk-preview': MockInjuryRiskPreview,
    'session-check-in': MockSessionCheckIn,
};

const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const BlogPostPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const post = slug ? getBlogPostBySlug(slug) : undefined;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('fade-in-up');
                }
            });
        }, observerOptions);

        document.querySelectorAll('.observe-fade').forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, [slug]);

    if (!post) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif" }}>
                <GlobalStyles />
                <LandingHeader highlightColor={highlightColor} />
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
                    <p className="text-gray-400 mb-8">The blog post you are looking for does not exist.</p>
                    <Link
                        to="/blog"
                        className="px-6 py-3 text-sm font-semibold transition-all duration-200"
                        style={{
                            backgroundColor: 'transparent',
                            color: 'white',
                            border: '1px solid white',
                            borderRadius: '0',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.color = 'black';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'white';
                        }}
                    >
                        Back to Blog
                    </Link>
                </div>
                <LandingFooter highlightColor={highlightColor} />
            </div>
        );
    }

    // JSON-LD structured data for SEO / AI crawlers
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.metaDescription,
        author: {
            '@type': 'Organization',
            name: post.author,
        },
        publisher: {
            '@type': 'Organization',
            name: 'CGM Sports',
            url: 'https://cgmsports.com',
        },
        image: post.heroImage,
        datePublished: post.date,
        url: `https://cgmsports.com/blog/${post.slug}`,
        mainEntityOfPage: `https://cgmsports.com/blog/${post.slug}`,
    };

    // Find related posts (same category, excluding current)
    const relatedPosts = blogPosts
        .filter(p => p.category === post.category && p.slug !== post.slug)
        .slice(0, 2);

    return (
        <div className="min-h-screen bg-black text-white transition-colors duration-300" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif" }}>
            <Helmet>
                <title>{post.title} - CGM Sports Blog</title>
                <meta name="title" content={`${post.title} - CGM Sports Blog`} />
                <meta name="description" content={post.metaDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://cgmsports.com/blog/${post.slug}`} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.metaDescription} />
                <meta property="og:image" content={post.heroImage} />
                <meta property="og:site_name" content="CGM Sports" />
                <meta property="article:published_time" content={post.date} />
                <meta property="article:author" content={post.author} />
                <meta property="article:section" content={post.category} />
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content={post.title} />
                <meta property="twitter:description" content={post.metaDescription} />
                <meta property="twitter:image" content={post.heroImage} />
                <link rel="canonical" href={`https://cgmsports.com/blog/${post.slug}`} />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd).replace(/</g, '\\u003c')}
                </script>
            </Helmet>
            <GlobalStyles />
            <LandingHeader highlightColor={highlightColor} />

            <article className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Back link */}
                    <button
                        onClick={() => navigate('/blog')}
                        className="text-sm text-gray-500 hover:text-white transition-colors mb-8 inline-flex items-center gap-2 cursor-pointer"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Blog
                    </button>

                    {/* Header */}
                    <header className="mb-12 observe-fade opacity-0">
                        <span className="text-xs uppercase tracking-widest text-gray-500">{post.category}</span>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4 leading-tight">
                            {post.title}
                        </h1>
                        <p className="text-lg text-gray-300 mb-6">{post.subtitle}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>{post.author}</span>
                            <span>·</span>
                            <time dateTime={post.date}>{formatDate(post.date)}</time>
                        </div>
                    </header>

                    {/* Hero image */}
                    <div className="mb-12 overflow-hidden observe-fade opacity-0">
                        <img
                            src={post.heroImage}
                            alt={post.title}
                            className="w-full h-64 md:h-96 object-cover"
                            loading="eager"
                        />
                    </div>

                    <div className="h-px bg-neutral-800 mb-12" />

                    {/* Body sections */}
                    <div className="space-y-12">
                        {post.sections.map((section, idx) => (
                            <section key={idx} className="observe-fade opacity-0">
                                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{section.heading}</h3>
                                <p className="text-gray-300 leading-relaxed mb-6">{section.content}</p>
                                {section.bullets && (
                                    <ul className="space-y-4">
                                        {section.bullets.map((bullet, bIdx) => (
                                            <li key={bIdx} className="flex gap-3">
                                                <span className="text-white mt-1.5 flex-shrink-0">
                                                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                                                        <rect width="12" height="12" />
                                                    </svg>
                                                </span>
                                                <div>
                                                    <span className="font-semibold text-white">{bullet.lead}:</span>{' '}
                                                    <span className="text-gray-300">{bullet.detail}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {section.visualComponent && VISUAL_COMPONENTS[section.visualComponent] && (
                                    <div className="mt-8">
                                        {React.createElement(VISUAL_COMPONENTS[section.visualComponent])}
                                    </div>
                                )}
                            </section>
                        ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-16 pt-12 border-t border-neutral-800 observe-fade opacity-0">
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-3">{post.cta.heading}</h3>
                        <p className="text-gray-300 mb-6">{post.cta.content}</p>
                        <Link
                            to={post.cta.buttonLink}
                            className="inline-block px-6 py-3 text-sm font-semibold transition-all duration-200"
                            style={{
                                backgroundColor: 'transparent',
                                color: 'white',
                                border: '1px solid white',
                                borderRadius: '0',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.color = 'black';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = 'white';
                            }}
                        >
                            {post.cta.buttonText}
                        </Link>
                    </div>

                    {/* Related posts */}
                    {relatedPosts.length > 0 && (
                        <div className="mt-20 pt-12 border-t border-neutral-800">
                            <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-6">More in {post.category}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {relatedPosts.map((related) => (
                                    <Link
                                        key={related.slug}
                                        to={`/blog/${related.slug}`}
                                        className="block group"
                                    >
                                        <div className="bg-neutral-900/50 border border-neutral-800 transition-all duration-300 group-hover:border-neutral-600 group-hover:bg-neutral-900 overflow-hidden">
                                            <div className="h-36 overflow-hidden">
                                                <img
                                                    src={related.heroImage}
                                                    alt={related.title}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="p-6">
                                                <span className="text-xs text-gray-600">{formatDate(related.date)}</span>
                                                <h5 className="text-base font-bold text-white mt-2 group-hover:text-gray-200 transition-colors leading-snug">
                                                    {related.title}
                                                </h5>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </article>

            <LandingFooter highlightColor={highlightColor} />
        </div>
    );
};

export default BlogPostPage;
